import {Component, HostListener, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Select, Store} from '@ngxs/store';
import {AuthState} from '../../../shared/states/auth/auth.state';
import {Observable} from 'rxjs/internal/Observable';
import {Login, SetPharmacyId} from '../../../shared/states/auth/auth.actions';
import {Router} from '@angular/router';
import {filter, take, withLatestFrom} from 'rxjs/operators';
import {CustomEmailValidator} from '../../../shared/validators/custom-email.validator';
import {GetUserPharmacies, SetCurrentPharmacy} from '../../../shared/states/pharmacy/pharmacy.actions';
import {PharmacyState} from '../../../shared/states/pharmacy/pharmacy.state';
import {IPharmacy} from '../../../shared/interfaces/pharmacy.interface';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss', './../../auth.scss'],
})
export class LoginComponent implements OnInit {
  loginForm: UntypedFormGroup;
  loginError: string;

  @ViewChild('emailInput') emailInput;
  @ViewChild('passwordInput') passwordInput;
  @ViewChild('forgotPasswordRef') forgotPasswordRef;
  @ViewChild('createAccountRef') createAccountRef;
  @ViewChild('submitBtn') submitBtn;

  @Select(AuthState.loading) loading$: Observable<boolean | undefined>;
  @Select(AuthState.userId) userId$: Observable<string>;

  @Select(PharmacyState.pharmacies) pharmacies$: Observable<IPharmacy[]>;

  @HostListener('keydown.tab', ['$event'])
  keydownTab(ev: KeyboardEvent): void {
    if (this.submitBtn.isFocused) {
      setTimeout(() => this.forgotPasswordRef.nativeElement.focus());
    } else if (this.forgotPasswordRef.nativeElement.isFocused) {
      setTimeout(() => this.createAccountRef.nativeElement.focus());
    }
  }

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private router: Router,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, CustomEmailValidator]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.loginForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.loginError) {
          this.loginError = null;
        }
      });
  }

  login(): void {
    if (this.loginForm.valid) {
      this.loginError = null;
      this.store.dispatch(new Login(this.loginForm.value))
        .pipe(withLatestFrom(this.userId$), filter(id => !!id))
        .subscribe(([_, id]) => {
          if (id) {
            this.getPharmacies();
          } else {
            this.loginError = 'Invalid credentials';
          }
        });
    }
  }

  getPharmacies(): void {
    this.store.dispatch(new GetUserPharmacies())
      .pipe(take(1), withLatestFrom(this.pharmacies$))
      .subscribe(([_, pharmacies]) => {
        if (pharmacies.length === 0) {
          this.router.navigateByUrl('/dashboard/set-up');
        } else if (pharmacies.length === 1) {
          this.store.dispatch(new SetPharmacyId(pharmacies[0].id));
          this.store.dispatch(new SetCurrentPharmacy(pharmacies[0].id));
          this.router.navigateByUrl('dashboard');
        } else {
          this.router.navigateByUrl('auth/select-pharmacy');
        }
      });
  }

  focusInput(inputName: string): void {
    setTimeout(() => this[inputName] && this[inputName]?.appInput?.nativeElement?.focus());
  }
}
